import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  Typography,
  Stack,
  Checkbox,
  TextField,
  Alert,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { api } from "../../services/api";
import { useParams } from "react-router-dom";
import { ImpresionContext } from "../../providers/Impresion";
import { toast } from "react-toastify";

const BRAND = "#5656ff";
const DARK = "#0b0062";
const SECONDARY = "#4B4D63";

const OptionRow = ({ checked, onChange, title, description }) => (
  <Box
    onClick={onChange}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      p: 2,
      borderRadius: "10px",
      border: `1.5px solid ${checked ? BRAND : "rgba(75,77,99,0.18)"}`,
      bgcolor: checked ? "rgba(86,86,255,0.04)" : "white",
      cursor: "pointer",
      transition: "border-color 200ms, background-color 200ms",
      "&:hover": { borderColor: BRAND },
    }}
  >
    <Checkbox
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      size="small"
      sx={{
        p: 0,
        mt: "2px",
        color: "rgba(75,77,99,0.4)",
        "&.Mui-checked": { color: BRAND },
      }}
    />
    <Stack spacing={0.3}>
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 600,
          fontSize: "14px",
          color: DARK,
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
          color: SECONDARY,
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    </Stack>
  </Box>
);

const SelectTarifa = ({ open, onClose, onConfirm, onSave, elements, dimensionContainer }) => {
  const [loading, setLoading] = useState(true);
  const [tarifasTickets, setTarifasTickets] = useState([]);
  const [tarifasVouchers, setTarifasVouchers] = useState([]);
  const [selectedTarifa, setSelectedTarifa] = useState("");
  const [error, setError] = useState("");
  const param = useParams();

  const [guardarComoPlantilla, setGuardarComoPlantilla] = useState(false);
  const [duplicarEnTarifa, setDuplicarEnTarifa] = useState(true);
  const [nombrePlantilla, setNombrePlantilla] = useState("");

  const { cargarPlantillasUsuario, plantillasUsuario } = useContext(ImpresionContext);

  const plantillaExistente = plantillasUsuario.find(
    (p) => p.nombre.toLowerCase() === nombrePlantilla.trim().toLowerCase()
  );

  useEffect(() => {
    const fetchTarifas = async () => {
      setLoading(true);
      try {
        const response = await api.tarifas.getAll(param.id, "", 100, 0);
        if (response.status === "success" && response.data.tarifas) {
          setTarifasTickets(response.data.tarifas.filter((t) => t.idtipo === 1));
          setTarifasVouchers(response.data.tarifas.filter((t) => t.idtipo === 2));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchTarifas();
      setSelectedTarifa("");
      setGuardarComoPlantilla(false);
      setDuplicarEnTarifa(true);
      setNombrePlantilla("");
      setError("");
    }
  }, [open, param.id]);

  const handleConfirm = async () => {
    setError("");
    setLoading(true);

    try {
      if (duplicarEnTarifa && !selectedTarifa) {
        setError("Debes seleccionar una tarifa destino");
        setLoading(false);
        return;
      }
      if (guardarComoPlantilla && !nombrePlantilla.trim()) {
        setError("Debes ingresar un nombre para la plantilla");
        setLoading(false);
        return;
      }
      if (!duplicarEnTarifa && !guardarComoPlantilla) {
        setError("Debes seleccionar al menos una opción");
        setLoading(false);
        return;
      }

      if (onSave) await onSave();

      if (guardarComoPlantilla) {
        const plantillaData = {
          nombre: nombrePlantilla.trim(),
          json: JSON.stringify({
            type: {
              type: dimensionContainer.type,
              width: dimensionContainer.width,
              height: dimensionContainer.height,
            },
            elements: elements || [],
          }),
        };
        const result = await api.plantillas.createPlantilla(plantillaData);
        if (result?.status === "success" && cargarPlantillasUsuario) {
          await cargarPlantillasUsuario();
        }
      }

      if (duplicarEnTarifa && selectedTarifa) {
        const allTarifas = [...tarifasTickets, ...tarifasVouchers];
        const tarifa = allTarifas.find((t) => t.id === selectedTarifa);
        if (tarifa) await onConfirm(tarifa);
      }

      onClose();
    } catch (err) {
      setError("Error al procesar la solicitud");
      toast.error("Error al procesar la solicitud: " + err.message, {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: "14px", p: 0.5 },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Bricolage Grotesque, sans-serif",
          fontWeight: 700,
          fontSize: "20px",
          color: DARK,
          pb: 0,
        }}
      >
        Guardar como...
      </DialogTitle>

      <DialogContent sx={{ pt: "16px !important" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px", fontSize: "13px" }}>
            {error}
          </Alert>
        )}

        <Stack spacing={1.5}>
          <OptionRow
            checked={guardarComoPlantilla}
            onChange={() => setGuardarComoPlantilla((v) => !v)}
            title="Guardar como plantilla"
            description="Podrás reutilizar este diseño en otros eventos"
          />

          {guardarComoPlantilla && (
            <TextField
              label="Nombre de la plantilla"
              fullWidth
              value={nombrePlantilla}
              onChange={(e) => setNombrePlantilla(e.target.value)}
              placeholder="Ej: Pulsera - Recepciones"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: BRAND,
                },
                "& .MuiInputLabel-root.Mui-focused": { color: BRAND },
              }}
              helperText={
                plantillaExistente
                  ? `Sobreescribirá la plantilla existente "${plantillaExistente.nombre}"`
                  : 'Si usás el mismo nombre, se actualizará la plantilla existente'
              }
              FormHelperTextProps={{
                sx: {
                  color: plantillaExistente ? "warning.main" : SECONDARY,
                  fontSize: "11px",
                },
              }}
            />
          )}

          <OptionRow
            checked={duplicarEnTarifa}
            onChange={() => setDuplicarEnTarifa((v) => !v)}
            title="Duplicar en otra tarifa"
            description="Crear una copia de este diseño para otra tarifa del mismo evento"
          />

          {duplicarEnTarifa && (
            loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} sx={{ color: BRAND }} />
              </Box>
            ) : (
              <FormControl fullWidth size="small">
                <Select
                  value={selectedTarifa}
                  displayEmpty
                  onChange={(e) => setSelectedTarifa(e.target.value)}
                  sx={{
                    borderRadius: "8px",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "14px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                  }}
                  renderValue={(val) =>
                    val
                      ? [...tarifasTickets, ...tarifasVouchers].find((t) => t.id === val)?.concepto
                      : <span style={{ color: SECONDARY, fontSize: "14px" }}>Tarifa destino</span>
                  }
                >
                  <ListSubheader sx={{ fontFamily: "Inter, sans-serif", fontSize: "11px", color: SECONDARY, lineHeight: "28px" }}>
                    Tickets
                  </ListSubheader>
                  {tarifasTickets.length > 0 ? (
                    tarifasTickets.map((t) => (
                      <MenuItem key={t.id} value={t.id} sx={{ fontFamily: "Inter, sans-serif", fontSize: "14px" }}>
                        {t.concepto}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled sx={{ fontFamily: "Inter, sans-serif", fontSize: "13px" }}>
                      No hay tickets
                    </MenuItem>
                  )}
                  <ListSubheader sx={{ fontFamily: "Inter, sans-serif", fontSize: "11px", color: SECONDARY, lineHeight: "28px" }}>
                    Vouchers
                  </ListSubheader>
                  {tarifasVouchers.length > 0 ? (
                    tarifasVouchers.map((v) => (
                      <MenuItem key={v.id} value={v.id} sx={{ fontFamily: "Inter, sans-serif", fontSize: "14px" }}>
                        {v.concepto}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled sx={{ fontFamily: "Inter, sans-serif", fontSize: "13px" }}>
                      No hay vouchers
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "14px",
            color: SECONDARY,
            textTransform: "none",
            borderRadius: "8px",
            px: 2,
            "&:hover": { bgcolor: "rgba(75,77,99,0.06)" },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "14px",
            textTransform: "none",
            borderRadius: "8px",
            bgcolor: BRAND,
            px: 3,
            "&:hover": { bgcolor: "#4b4be8" },
            "&.Mui-disabled": { bgcolor: "rgba(86,86,255,0.3)", color: "white" },
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectTarifa;
